import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { startLoadingCatalogModules } from '../../app/actions/catalogModuleActions';
import { startLoadingCatalogCiiusByLevel1 } from '../../app/actions/catalogCiiuActions';
import { startLoadingCountries } from '../../app/actions/countryActions';
import "react-datepicker/dist/react-datepicker.css";
import { startLoadingCatalogsAll } from '../../app/actions/catalogActions';
import CreatableSelect from 'react-select/creatable';
import FieldFeedback from './FieldFeedback';
import { startRegisterSuggestCase } from '../../app/actions/suggestCaseActions';

const EXCLUDED_CODES = ['V', 'W'];

const VALIDATORS = {
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    url: /^(https?:\/\/)?([\w\d-]+\.)+[\w-]+(\/[\w- .\/?%&=]*)?$/,
    phone: /^(\+?\d{1,3}[- ]?)?\(?\d{3}\)?[- ]?\d{3}[- ]?\d{2,10}$/,
    // Solo letras, números y espacios
    //alphaNumeric: /^[a-zA-Z0-9ñÑáéíóúÁÉÍÓÚ\s]+$/,
    alphaNumeric: /^[a-zA-Z0-9ñÑáéíóúÁÉÍÓÚ\s.,;:¡!¿?()\-—"']+$/,
    // Nombres: Permite letras, tildes, ñ, puntos (ej. S.A.) y comas
    generalName: /^[a-zA-ZñÑáéíóúÁÉÍÓÚ\s.,&()\-]+$/,
    //Fecha
    date: /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/,
};

const FIELD_CONFIG = {
    //1 module
    catalogmodule_id: { isSelect: true, required: true },
    //2 name
    case_name: { max: 400, required: true, isAlphaNumeric: true },
    //3 sector
    catalogciius_id: { isSelect: true, required: true },
    //4 country
    catalogcountries_id: { isSelect: true, required: true },
    //5 Entidad Generadora
    entity_gen_name: { max: 200, required: true, isNameEntity: true },
    entity_gen_email: { max: 100, required: true, isEmail: true },
    entity_gen_web: { max: 255, required: true, isUrl: true },
    entity_gen_phone: { max: 50, required: false, isPhoneNumber: true },
    //6
    case_url: { max: 500, required: false, isUrl: true },
    //7 Medio
    catalogmediatype_id: { isSelect: true, required: false },
    media_name: { max: 200, required: false, isNameMedia: true },
    media_date: { required: false, isDate: true },
    //medioPais: { isSelect: true, required: false },
    catalogmediacountries_id: { isSelect: true, required: false },
    media_city: { max: 100, required: false },
    //media_city: { max: 100, required: false },
    //8 Remitente
    sender_name: { max: 150, required: true, isNamePerson: true },
    //sender_relation: { max: 100, required: false },
    sender_email: { max: 100, required: true, isEmail: true },
    sender_phone: { max: 50, required: false, isPhoneNumber: true },
};

const FIELD_CONFIG_VISION = {
    //1 module
    catalogmodule_id: { isSelect: true, required: true },
    // Visiones Analíticas Flow
    question_name: { max: 1000, required: true, isAlphaNumeric: true },
    //8 Remitente
    sender_name: { max: 150, required: true, isNamePerson: true },
    //sender_relation: { max: 100, required: false },
    sender_email: { max: 100, required: true, isEmail: true },
    sender_phone: { max: 50, required: false, isPhoneNumber: true },
};

const formDataInitialState = {
    catalogmodule_id: null,
    case_name: '',
    catalogciius_id: null,
    catalogcountries_id: null,
    entity_gen_name: '', entity_gen_email: '', entity_gen_web: '', entity_gen_phone: '',
    //entity_cont_name: '', entity_cont_address: '', entity_cont_email: '', entity_cont_web: '',
    case_url: '',
    catalogmediatype_id: null,
    media_name: '',
    media_date: null,
    catalogmediacountries_id: null,
    media_city: '',
    sender_name: '', //sender_relation: '', 
    sender_email: '', sender_phone: '',
    sender_wants_reference: '',
    question_name: ''
}

const EndUserSuggestFormScreen = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { catalogModules } = useSelector((state) => state.catalogModule);
    const { listCatalogCiiusLevel1 } = useSelector((state) => state.catalogCiiu);
    const { listFullCountries } = useSelector((state) => state.locationCountry);
    const { listMediaTypes } = useSelector((state) => state.catalog);

    const { loadingSuggestCase } = useSelector((state) => state.suggestCase || {});

    // --- States ---
    const [normalFlow, setNormalFlow] = useState(true);

    const currentConfig = normalFlow ? FIELD_CONFIG : FIELD_CONFIG_VISION;

    const [formData, setFormData] = useState(formDataInitialState);

    const [errors, setErrors] = useState({});
    const [counters, setCounters] = useState({});

    const entityKeyMap = {
        'technology': 'technology',
        'alternative': 'alternative',
        'experience': 'experience',
        'innovation': 'innovation',
        'assistance': 'assistance',
        'financial': 'financial',
        'regulation': 'regulation'
    };

    useEffect(() => {
        dispatch(startLoadingCatalogsAll());
        dispatch(startLoadingCatalogModules());
        dispatch(startLoadingCatalogCiiusByLevel1(1));
        dispatch(startLoadingCountries());
    }, [dispatch]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const config = currentConfig[name];
        //const config = FIELD_CONFIG[name];

        if (config) {
            setCounters((prev) => ({ ...prev, [name]: value.length }));

            // 2. Real-time Validation
            const errorMsg = validateField(name, value);
            //console.log("Error message for", name, ":", errorMsg);
            setErrors((prev) => ({ ...prev, [name]: errorMsg }));
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleModuleChange = (selected) => {
        //console.log("selected", selected);
        const isVisiones = selected?.key === 'vision';
        setNormalFlow(!isVisiones);
        setFormData({ ...formData, catalogmodule_id: selected });
    };

    const handleCiiuChange = (selected) => setFormData({ ...formData, catalogciius_id: selected });
    const handleCountryChange = (selected) => setFormData({ ...formData, catalogcountries_id: selected });
    const handleTipoMedioChange = (selected) => setFormData({ ...formData, catalogmediatype_id: selected });
    //const handleQuestionChange = (selected) => setFormData({ ...formData, preguntaSeleccionada: selected });
    const handleMedioPaisChange = (selected) => {
        setFormData({ ...formData, catalogmediacountries_id: selected });
    };

    const validateField = (name, value) => {
        const config = currentConfig[name];
        if (!config) return "";

        // --- 1. Validación de Selects ---
        if (config.isSelect) {
            if (config.required) {
                const isEmpty = config.isMulti ? (!value || value.length === 0) : !value;
                return isEmpty ? t("general.suggest.form.error-required") : "";
            }
            return "";
        }

        // --- 2. Preparación de String ---
        const stringValue = value ? value.toString().trim() : "";

        // --- 3. Validación de Requeridos y Largo Máximo ---
        if (config.required && !stringValue) {
            return t("general.suggest.form.error-required");
        }
        if (stringValue && stringValue.length > config.max) {
            return t("general.suggest.form.error-max-length", { max: config.max });
        }

        // --- 4. Validaciones de Formato (Regex) ---
        if (stringValue) {
            if (config.isEmail && !VALIDATORS.email.test(stringValue)) {
                return t("general.suggest.form.error-email-invalid");
            }
            if (config.isUrl && !VALIDATORS.url.test(stringValue)) {
                return t("general.suggest.form.error-url-invalid");
            }
            if (config.isPhoneNumber && !VALIDATORS.phone.test(stringValue)) {
                return t("general.suggest.form.error-phone-invalid");
            }
            if (config.isAlphaNumeric && !VALIDATORS.alphaNumeric.test(stringValue)) {
                return t("general.suggest.form.error-alphanumeric-invalid");
            }
            // Validación unificada para nombres (Entity, Media, Person)
            if ((config.isNameEntity || config.isNameMedia || config.isNamePerson) &&
                !VALIDATORS.generalName.test(stringValue)) {
                return t("general.suggest.form.error-name-invalid");
            }
            //Date
            //if (config.isDate && !VALIDATORS.date.test(stringValue)) {
            //    return t("general.suggest.form.error-date-invalid");
            //}

            if (config.isDate && isNaN(Date.parse(stringValue))) {
                return t("general.suggest.form.error-date-invalid");
            }
        }

        return "";
    };

    const validateAll = () => {
        const newErrors = {};
        let isValid = true;

        Object.keys(currentConfig).forEach((fieldName) => {
            const value = formData[fieldName];
            const error = validateField(fieldName, value);

            if (error) {
                newErrors[fieldName] = error;
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        //console.log("0000, handleSubmit, formData", formData);

        // Run global validation
        const isFormValid = validateAll();
        //console.log("isFormValid", isFormValid);

        if (isFormValid) {

            const dataToSend = {
                ...formData,
                media_date: formData.media_date ? formData.media_date.toISOString() : null
            };

            dispatch(startRegisterSuggestCase(dataToSend));

            //Reset form after submission
            setFormData(formDataInitialState);

        } else {
            // Scroll to the first error (optional UX improvement)
            const firstError = document.querySelector('.is-invalid');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            console.warn("El formulario tiene errores. Por favor, verifique los campos requeridos.");
        }
    };

    // console.log("1111, loadingSuggestCase",loadingSuggestCase);

    return (
        <div className="container mt-4 p-4 border bg-white shadow-sm">
            <h4 className="text-center mb-2 font-weight-bold text-uppercase">{t('general.suggest.form.title.suggest')}</h4>
            <p className="small text-muted mb-4 text-justify">{t('general.suggest.form.information')}</p>

            <h5 className="text-center mb-2 font-weight-bold text-uppercase">{t('general.suggest.form.title.guide')}</h5>

            <form onSubmit={handleSubmit}>
                {/* (1) Modulo */}
                <div className="mb-3">
                    <label className="form-label font-weight-bold">{t('general.suggest.form.module')}</label>
                    <Select
                        options={catalogModules}
                        onChange={handleModuleChange}
                        getOptionLabel={(opt) => t(`general.suggest.form-${opt.key}`)}
                        placeholder={t('general.suggest.form.module-placeholder')}
                        styles={{
                            control: (base) => ({
                                ...base,
                                borderColor: errors.catalogmodule_id ? "#dc3545" : base.borderColor,
                                '&:hover': { borderColor: errors.catalogmodule_id ? "#dc3545" : base.borderColor }
                            })
                        }}
                    />
                    <FieldFeedback
                        field="catalogmodule_id"
                        errors={errors}
                        counters={counters}
                        config={currentConfig}
                    />
                </div>

                {/* --- CONDITIONAL RENDER: QUESTIONS (When normalFlow is false) --- */}
                {!normalFlow && (
                    <div className="mb-3 animate__animated animate__fadeIn">
                        {/* 2 Pregunta */}
                        <div className="mb-3">
                            <label className="form-label font-weight-bold">{t('general.suggest.form.name-vision')}</label>
                            <textarea
                                name="question_name"
                                className={`form-control ${errors.question_name ? 'is-invalid' : ''}`}
                                value={formData.question_name}
                                rows={2}
                                onChange={handleInputChange} />
                            <FieldFeedback
                                field="question_name"
                                errors={errors}
                                counters={counters}
                                config={currentConfig}
                            />
                        </div>
                    </div>
                )}

                {/* --- NORMAL FLOW: POINTS 2 to 8 --- */}
                {normalFlow && (
                    <>
                        {/* 2 Nombre Caso */}
                        <div className="mb-3">
                            <label className="form-label font-weight-bold">{t('general.suggest.form.name-normal')}</label>
                            <textarea
                                name="case_name"
                                className={`form-control ${errors.case_name ? 'is-invalid' : ''}`}
                                value={formData.case_name}
                                rows={2}
                                onChange={handleInputChange} />
                            <FieldFeedback
                                field="case_name"
                                errors={errors}
                                counters={counters}
                                config={currentConfig}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label font-weight-bold">{t('general.suggest.form.sector')}</label>
                            <Select
                                options={listCatalogCiiusLevel1.filter(opt => !EXCLUDED_CODES.includes(opt.code))}
                                getOptionLabel={(opt) => `${opt.code} - ${opt.name}`}
                                onChange={handleCiiuChange}
                                placeholder={t('general.suggest.form.sector-placeholder')}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderColor: errors.catalogmodule_id ? "#dc3545" : base.borderColor,
                                        '&:hover': { borderColor: errors.catalogmodule_id ? "#dc3545" : base.borderColor }
                                    })
                                }}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label font-weight-bold">
                            {t(`general.suggest.form.country-${entityKeyMap[formData.catalogmodule_id?.key] || 'technology'}`)}
                            </label>
                            <CreatableSelect
                                isClearable
                                isSearchable
                                options={listFullCountries}
                                getOptionLabel={(opt) => opt.name || opt.label} // Soporta objetos de API y texto libre
                                getOptionValue={(opt) => opt.id || opt.value}
                                onChange={handleCountryChange}
                                placeholder={t('general.suggest.form.country-placeholder')}
                                formatCreateLabel={(inputValue) => `Agregar "${inputValue}"`} // Personaliza el texto de creación
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderColor: errors.catalogcountries_id ? "#dc3545" : base.borderColor,
                                        '&:hover': { borderColor: errors.catalogcountries_id ? "#dc3545" : base.borderColor }
                                    })
                                }}
                            />
                            <FieldFeedback
                                field="catalogcountries_id"
                                errors={errors}
                                counters={counters}
                                config={currentConfig}
                            />
                        </div>

                        {/* (5) Entidad Generadora */}
                        <div className="mb-4 p-3 border rounded bg-light">
                            {/*<label className="form-label font-weight-bold d-block mb-3">{t('general.suggest.form.entity-generator')}</label>*/}
                            <label className="form-label font-weight-bold d-block mb-3">
                            {t(`general.suggest.form.entity-generator-${entityKeyMap[formData.catalogmodule_id?.key] || 'technology'}`)} </label>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="small">{t('general.suggest.form.entity-generator-name')}</label>
                                    <input name="entity_gen_name"
                                        onChange={handleInputChange}
                                        className={`form-control ${errors.entity_gen_name ? 'is-invalid' : ''}`}
                                        value={formData.entity_gen_name}
                                        placeholder={t('general.suggest.form.entity-generator-name-placeholder')}
                                    />
                                    <FieldFeedback
                                        field="entity_gen_name"
                                        errors={errors}
                                        counters={counters}
                                        config={currentConfig}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="small">{t('general.suggest.form.entity-generator-email')}</label>
                                    <input name="entity_gen_email"
                                        onChange={handleInputChange}
                                        className={`form-control ${errors.entity_gen_email ? 'is-invalid' : ''}`}
                                        value={formData.entity_gen_email}
                                        placeholder={t('general.suggest.form.entity-generator-email-placeholder')}
                                    />
                                    <FieldFeedback
                                        field="entity_gen_email"
                                        errors={errors}
                                        counters={counters}
                                        config={currentConfig}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="small">{t('general.suggest.form.entity-generator-web')}</label>
                                    <input name="entity_gen_web"
                                        onChange={handleInputChange}
                                        className={`form-control ${errors.entity_gen_web ? 'is-invalid' : ''}`}
                                        value={formData.entity_gen_web}
                                        placeholder={t('general.suggest.form.entity-generator-web-placeholder')}
                                    />
                                    <FieldFeedback
                                        field="entity_gen_web"
                                        errors={errors}
                                        counters={counters}
                                        config={currentConfig}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="small">{t('general.suggest.form.entity-generator-phone')}</label>
                                    <input name="entity_gen_phone"
                                        onChange={handleInputChange}
                                        className={`form-control ${errors.entity_gen_phone ? 'is-invalid' : ''}`}
                                        value={formData.entity_gen_phone}
                                        placeholder={t('general.suggest.form.entity-generator-phone-placeholder')}
                                    />
                                    <FieldFeedback
                                        field="entity_gen_phone"
                                        errors={errors}
                                        counters={counters}
                                        config={currentConfig}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* (6) URL*/}
                        <div className="mb-4">
                            <label className="form-label font-weight-bold">{t('general.suggest.form.url')}</label>
                            <input name="case_url"
                                placeholder={t('general.suggest.form.url-placeholder')}
                                onChange={handleInputChange}
                                className={`form-control ${errors.case_url ? 'is-invalid' : ''}`}
                                value={formData.case_url}
                            />
                            <FieldFeedback
                                field="case_url"
                                errors={errors}
                                counters={counters}
                                config={currentConfig}
                            />
                        </div>

                        {/* (7) Identidad Difusión */}
                        <div className="mb-4 p-3 border rounded bg-light">
                            <label className="form-label font-weight-bold d-block mb-3">{t('general.suggest.form.identity-media')}</label>
                            <div className="row g-3">
                                <div
                                    className="col-md-6"><label className="small">{t('general.suggest.form.identity-media-type')}</label>
                                    <CreatableSelect
                                        options={listMediaTypes}
                                        onChange={handleTipoMedioChange}
                                        formatCreateLabel={(inputValue) => `Agregar "${inputValue}"`}
                                        getOptionLabel={(option) => `${option.description}`}
                                        getOptionValue={(option) => `${option}`}
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                borderColor: errors.catalogmediatype_id ? "#dc3545" : base.borderColor,
                                                '&:hover': { borderColor: errors.catalogmediatype_id ? "#dc3545" : base.borderColor }
                                            })
                                        }}
                                    />

                                </div>
                                <div className="col-md-6">
                                    <label className="small">{t('general.suggest.form.identity-media-name')}</label>
                                    <input name="media_name"
                                        onChange={handleInputChange}
                                        className={`form-control ${errors.media_name ? 'is-invalid' : ''}`}
                                        value={formData.media_name}
                                        placeholder={t('general.suggest.form.identity-media-name-placeholder')}
                                    />
                                    <FieldFeedback
                                        field="media_name"
                                        errors={errors}
                                        counters={counters}
                                        config={currentConfig}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="small">
                                        {t('general.suggest.form.identity-media-date-publication')}
                                    </label>
                                    <div className={errors.media_date ? "is-invalid-container" : ""}>
                                        <DatePicker
                                            // Agregamos dinámicamente la clase de error de Bootstrap
                                            className={`form-control ${errors.media_date ? 'is-invalid' : ''}`}
                                            selected={formData.media_date}
                                            onChange={(date) => {
                                                setFormData({
                                                    ...formData,
                                                    media_date: date
                                                });
                                            }}
                                            dateFormat="dd/MM/yyyy"
                                            isClearable
                                            placeholderText={t('general.suggest.form.select-date-placeholder')} // Usando t()
                                        />
                                    </div>

                                    {/* Agregamos el feedback igual que en los otros campos */}
                                    <FieldFeedback
                                        field="media_date"
                                        errors={errors}
                                        counters={counters}
                                        config={currentConfig}
                                    />
                                </div>

                                {/* Pais ciudad */}
                                <div className="col-md-6 mt-2">
                                    <label className="small">{t('general.suggest.form.identity-media-country-city')}</label>
                                    <div className="d-flex align-items-center">
                                        {/* Country Select */}
                                        <div style={{ flex: 1 }}>
                                            <CreatableSelect
                                                isClearable
                                                isSearchable
                                                options={listFullCountries}
                                                getOptionLabel={(opt) => opt.name || opt.label}
                                                getOptionValue={(opt) => opt.id || opt.value}
                                                onChange={handleMedioPaisChange}
                                                placeholder={t('general.suggest.form.identity-media-country-placeholder')}
                                                formatCreateLabel={(inputValue) => `Agregar "${inputValue}"`}
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        borderTopRightRadius: 0,
                                                        borderBottomRightRadius: 0,
                                                        borderRight: 0
                                                    })
                                                }}
                                            />
                                        </div>

                                        {/* Visual Separator */}
                                        <span className="px-2 bg-light border-top border-bottom d-flex align-items-center justify-content-center"
                                            style={{ height: '38px', fontSize: '1.2rem', color: '#6c757d' }}>
                                            ,
                                        </span>

                                        {/* City Input */}
                                        <div style={{ flex: 1 }}>
                                            <input
                                                name="media_city"
                                                className="form-control"
                                                placeholder={t('general.suggest.form.identity-media-city-placeholder')}
                                                onChange={handleInputChange}
                                                style={{
                                                    borderTopLeftRadius: 0,
                                                    borderBottomLeftRadius: 0,
                                                    height: '38px'
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* (8) Persona o Institución (Shared) */}
                <div className="mb-4 p-3 border rounded bg-light">
                    <label className="form-label font-weight-bold d-block mb-3">{normalFlow ? t('general.suggest.form.person-institution') : t('general.suggest.form.person-institution-vision')}</label>
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="small">{t('general.suggest.form.person-institution-name')}</label>
                            <input name="sender_name"
                                onChange={handleInputChange}
                                className={`form-control ${errors.sender_name ? 'is-invalid' : ''}`}
                                value={formData.sender_name}
                                placeholder={t('general.suggest.form.person-institution-name-placeholder')}
                            />
                            <FieldFeedback
                                field="sender_name"
                                errors={errors}
                                counters={counters}
                                config={currentConfig}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="small">{t('general.suggest.form.person-institution-email')}</label>
                            <input name="sender_email"
                                onChange={handleInputChange}
                                className={`form-control ${errors.sender_email ? 'is-invalid' : ''}`}
                                value={formData.sender_email}
                                placeholder={t('general.suggest.form.person-institution-email-placeholder')}
                            />
                            <FieldFeedback
                                field="sender_email"
                                errors={errors}
                                counters={counters}
                                config={currentConfig}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="small">{t('general.suggest.form.person-institution-phone')}</label>
                            <input name="sender_phone"
                                onChange={handleInputChange}
                                className={`form-control ${errors.sender_phone ? 'is-invalid' : ''}`}
                                value={formData.sender_phone}
                                placeholder={t('general.suggest.form.person-institution-phone-placeholder')}
                            />
                            <FieldFeedback
                                field="sender_phone"
                                errors={errors}
                                counters={counters}
                                config={currentConfig}
                            />
                        </div>
                    </div>
                </div>

                <div className="p-3 bg-secondary text-white rounded small mb-4">
                    <strong> {t('general.suggest.form.discharge')} </strong> {t('general.suggest.form.discharge-info')}
                </div>

                <div className="mb-4 p-3 border rounded bg-light">
                    <strong> {t('general.suggest.form.send-information')} </strong> {t('general.suggest.form.email')}
                </div>

                {/*<button type="submit" className="btn btn-primary w-100 py-2">{t('general.suggest.form.send-suggest')}</button>*/}
                <button
                    type="submit"
                    className="btn btn-primary w-100 py-2"
                    disabled={loadingSuggestCase} >
                    {loadingSuggestCase ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            {t('general.suggest.form.send-suggest-loading')}
                        </>
                    ) : (
                        t('general.suggest.form.send-suggest')
                    )}
                </button>

            </form>
        </div>
    );
};

export default EndUserSuggestFormScreen;
